import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { CompletionRuleEvaluator } from '../../services/CompletionRuleEvaluator';

/**
 * Diagnostic tool to debug completion rule issues
 * Shows exactly why tasks aren't auto-completing
 */
export function CompletionRuleDiagnostic() {
  const { familyUsers } = useFamilyContext();
  const [selectedUserId, setSelectedUserId] = useState('');
  const [diagnostics, setDiagnostics] = useState(null);
  const [loading, setLoading] = useState(false);

  const children = familyUsers?.filter(user => user.type !== 'parent') || [];

  const runDiagnostic = async () => {
    if (!selectedUserId) {
      alert('Please select a student');
      return;
    }

    setLoading(true);
    setDiagnostics(null);

    try {
      const results = {
        tasks: [],
        userData: null,
        ruleEvaluations: []
      };

      // 1. Get all tasks with completion rules
      const { data: tasks, error: tasksError } = await supabase
        .from('timeline_items')
        .select('*')
        .eq('user_id', selectedUserId)
        .not('completion_rule', 'is', null);

      if (tasksError) throw tasksError;

      console.log('📋 Tasks with completion_rule:', tasks);
      results.tasks = tasks || [];

      // 2. Get user data (same as CompletionRuleEvaluator does)
      const userData = await CompletionRuleEvaluator.getUserData(selectedUserId);
      console.log('👤 User data:', userData);
      results.userData = userData;

      // 3. Evaluate each rule and show WHY it passes/fails
      for (const task of tasks || []) {
        console.log(`\n🔍 Evaluating task: ${task.title}`);
        console.log(`  Rule: ${task.completion_rule}`);
        console.log(`  Current status: completed=${task.completed}, workflow_phase=${task.workflow_phase}`);

        const shouldComplete = await CompletionRuleEvaluator.evaluateCompletionRule(
          task.completion_rule,
          userData,
          task
        );

        results.ruleEvaluations.push({
          taskId: task.id,
          taskTitle: task.title,
          completionRule: task.completion_rule,
          currentlyCompleted: task.completed,
          shouldComplete,
          reason: getRuleEvaluationReason(task.completion_rule, userData, shouldComplete)
        });

        console.log(`  Should complete: ${shouldComplete}`);
      }

      setDiagnostics(results);

    } catch (error) {
      console.error('Diagnostic error:', error);
      alert('Error running diagnostic: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getRuleEvaluationReason = (rule, userData, result) => {
    switch (rule) {
      case 'finalize_reach_schools':
        const reachCount = userData.collegesByCategory.reach || 0;
        const reachLocked = userData.lockedCategories?.reach || false;
        return `Reach schools: ${reachCount}/4, Locked: ${reachLocked} → ${result ? '✅ Pass' : '❌ Fail'}`;

      case 'finalize_target_schools':
        const targetCount = userData.collegesByCategory.target || 0;
        const targetLocked = userData.lockedCategories?.target || false;
        return `Target schools: ${targetCount}/4, Locked: ${targetLocked} → ${result ? '✅ Pass' : '❌ Fail'}`;

      case 'finalize_safety_schools':
        const safetyCount = userData.collegesByCategory.safety || 0;
        const safetyLocked = userData.lockedCategories?.safety || false;
        return `Safety schools: ${safetyCount}/4, Locked: ${safetyLocked} → ${result ? '✅ Pass' : '❌ Fail'}`;

      case 'strategic_assessment_complete':
      case 'take_strategic_profile_assessment':
        const hasAssessment = userData.assessment !== null;
        return `Assessment exists: ${hasAssessment} → ${result ? '✅ Pass' : '❌ Fail'}`;

      default:
        return result ? '✅ Pass' : '❌ Fail';
    }
  };

  return (
    <div style={{
      background: '#fff',
      border: '2px solid #f59e0b',
      borderRadius: '8px',
      padding: '20px',
      maxWidth: '100%',
      maxHeight: '80vh',
      overflow: 'auto',
      boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
    }}>
      <h3 style={{ margin: '0 0 10px 0', color: '#f59e0b' }}>Diagnostic Tool</h3>

      <div style={{ marginBottom: '15px' }}>
        <select
          value={selectedUserId}
          onChange={(e) => setSelectedUserId(e.target.value)}
          style={{
            width: '100%',
            padding: '8px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            marginBottom: '10px'
          }}
        >
          <option value="">Select a student...</option>
          {children.map(child => (
            <option key={child.id} value={child.id}>
              {child.name || child.displayName}
            </option>
          ))}
        </select>

        <button
          onClick={runDiagnostic}
          disabled={loading || !selectedUserId}
          style={{
            width: '100%',
            padding: '10px',
            background: loading ? '#ccc' : '#f59e0b',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontWeight: 600
          }}
        >
          {loading ? 'Running Diagnostic...' : 'Run Diagnostic'}
        </button>
      </div>

      {diagnostics && (
        <div style={{ fontSize: '14px' }}>
          <div style={{
            background: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '4px',
            padding: '10px',
            marginBottom: '15px'
          }}>
            <strong>Summary:</strong><br />
            Tasks with completion_rule: {diagnostics.tasks.length}<br />
            College data loaded: {diagnostics.userData ? '✅' : '❌'}
          </div>

          <div style={{
            background: '#f3f4f6',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            padding: '10px',
            marginBottom: '15px'
          }}>
            <strong>User Data:</strong><br />
            Total colleges: {diagnostics.userData?.totalColleges || 0}<br />
            Reach: {diagnostics.userData?.collegesByCategory?.reach || 0}
            {diagnostics.userData?.lockedCategories?.reach ? ' 🔒' : ''}<br />
            Target: {diagnostics.userData?.collegesByCategory?.target || 0}
            {diagnostics.userData?.lockedCategories?.target ? ' 🔒' : ''}<br />
            Safety: {diagnostics.userData?.collegesByCategory?.safety || 0}
            {diagnostics.userData?.lockedCategories?.safety ? ' 🔒' : ''}<br />
            Assessment: {diagnostics.userData?.assessment ? '✅ Exists' : '❌ None'}
          </div>

          <div>
            <strong>Rule Evaluations:</strong>
            {diagnostics.ruleEvaluations.map((evaluation, idx) => (
              <div
                key={idx}
                style={{
                  background: evaluation.shouldComplete ? '#d1fae5' : '#fee2e2',
                  border: `1px solid ${evaluation.shouldComplete ? '#10b981' : '#ef4444'}`,
                  borderRadius: '4px',
                  padding: '10px',
                  marginTop: '10px'
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: '5px' }}>
                  {evaluation.taskTitle}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  Rule: <code>{evaluation.completionRule}</code><br />
                  Currently: {evaluation.currentlyCompleted ? 'Completed' : 'Not completed'}<br />
                  Should be: {evaluation.shouldComplete ? 'Completed' : 'Not completed'}<br />
                  <strong>{evaluation.reason}</strong>
                </div>
              </div>
            ))}
          </div>

          <div style={{
            marginTop: '15px',
            padding: '10px',
            background: '#eff6ff',
            border: '1px solid #3b82f6',
            borderRadius: '4px',
            fontSize: '12px'
          }}>
            <strong>💡 What to check:</strong><br />
            1. If tasks show as "should be completed" but aren't, the evaluator should mark them complete<br />
            2. If "should be not completed", check why the data doesn't match (locks, counts, etc.)<br />
            3. Check browser console for detailed logs
          </div>
        </div>
      )}
    </div>
  );
}

export default CompletionRuleDiagnostic;
